<template>
  <div class="so-detail">
    <KTCodePreview v-bind:title="'Chi tiết Phiếu dịch vụ'">
      <template v-slot:toolbar>
        <span v-if="order" class="so-detail-head">
          <b-badge :variant="statusVariant">{{ statusLabel }}</b-badge>
          <span class="so-detail-no">{{ order.orderNo }}</span>
        </span>
      </template>

      <template v-slot:preview>
        <b-overlay :show="loading" rounded>
          <div v-if="order">
            <!-- ============ Banner trạng thái kết thúc ============ -->
            <b-alert
              :show="isCancelled"
              variant="danger"
              class="d-flex align-items-center mb-3"
            >
              <i class="fas fa-ban mr-2"></i>
              <span>
                <b>Phiếu đã huỷ.</b>
                <span v-if="order.cancelReason">
                  Lý do: {{ order.cancelReason }}</span
                >
              </span>
            </b-alert>
            <b-alert
              :show="isReleased"
              variant="success"
              class="d-flex align-items-center mb-3"
            >
              <i class="fas fa-flag-checkered mr-2"></i>
              <b>Phiếu đã hoàn tất (đã xuất xưởng).</b>
            </b-alert>

            <!-- ============ Thanh tiến trình 7 bước ============ -->
            <div class="so-wizard" :class="{ 'so-wizard--muted': !stepIndex }">
              <div
                v-for="step in steps"
                :key="step.index"
                class="so-wizard-step"
                :class="stepClass(step.index)"
              >
                <div class="so-wizard-bullet">
                  <i v-if="stepState(step.index) === 'done'" class="fas fa-check"></i>
                  <span v-else>{{ step.index }}</span>
                </div>
                <div class="so-wizard-label">{{ step.label }}</div>
              </div>
            </div>

            <!-- ============ CTA bar (sinh từ state machine) ============ -->
            <div class="so-cta-bar">
              <div class="so-cta-left">
                <b-button
                  v-if="showQuoteLink"
                  size="sm"
                  variant="outline-primary"
                  @click="goQuote"
                >
                  <i class="fas fa-file-invoice-dollar"></i> Mở báo giá
                </b-button>
              </div>
              <div class="so-cta-right">
                <!-- Nút tiến bước — TẤT CẢ hành động từ state machine (REPAIRING = 2 nút: Tạo MRQ +
                     Chuyển nghiệm thu). KHÔNG dùng nextAdvance. Bước phát vật tư (mrqIssue) làm ở bảng MRQ. -->
                <b-button
                  v-for="action in advanceActions"
                  :key="action.to"
                  :variant="action.trigger === 'mrqCreate' ? 'outline-primary' : 'primary'"
                  class="mr-2"
                  :disabled="!action.enabled || !action.canDo || submitting"
                  :title="action.enabled ? '' : 'Bước này thuộc Phase ' + action.phase + ' — chưa triển khai'"
                  @click="onActionClick(action)"
                >
                  <b-spinner v-if="submitting" small />
                  {{ action.action }}
                  <span v-if="!action.enabled" class="so-phase-tag"
                    >(Phase {{ action.phase }})</span
                  >
                </b-button>
                <!-- Nút huỷ — hiện khi ma trận cho phép + có quyền CANCEL. -->
                <b-button
                  v-if="cancelRule.allowed && canCancel"
                  variant="outline-danger"
                  :disabled="!cancelRule.enabled || submitting"
                  :title="cancelRule.enabled ? '' : 'Huỷ ở trạng thái này cần xử lý hoàn vật tư/quyết toán (Phase ' + cancelRule.phase + ')'"
                  @click="openCancelModal"
                >
                  <i class="fas fa-ban"></i> Huỷ phiếu
                  <span v-if="!cancelRule.enabled" class="so-phase-tag"
                    >(Phase {{ cancelRule.phase }})</span
                  >
                </b-button>
              </div>
            </div>

            <!-- ============ Tabs ============ -->
            <b-tabs
              v-model="tabIndex"
              class="mt-3 so-detail-tabs"
              content-class="pt-3"
            >
              <!-- ---- Tab 1: Phiếu dịch vụ (read-only) ---- -->
              <b-tab title="Phiếu dịch vụ">
                <!-- Section 1: Thông tin chung -->
                <div class="so-card">
                  <h6 class="so-card-title">Thông tin chung</h6>
                  <b-row>
                    <b-col md="4" class="so-ro">
                      <span>Số phiếu:</span> <b>{{ order.orderNo }}</b>
                    </b-col>
                    <b-col md="4" class="so-ro">
                      <span>Ngày tạo:</span> {{ formatDate(order.createdAt) }}
                    </b-col>
                    <b-col md="4" class="so-ro">
                      <span>Loại phiếu:</span> {{ serviceTypeLabel }}
                    </b-col>
                    <b-col md="4" class="so-ro">
                      <span>Khách hàng:</span> {{ order.customerName }}
                    </b-col>
                    <b-col md="4" class="so-ro">
                      <span>Điện thoại:</span> {{ order.customerPhone }}
                    </b-col>
                    <b-col md="4" class="so-ro">
                      <span>Biển số:</span> {{ order.licensePlate }}
                    </b-col>
                    <b-col md="4" class="so-ro">
                      <span>Kiểu xe:</span> {{ order.vehicleModel }}
                    </b-col>
                    <b-col md="4" class="so-ro">
                      <span>Số khung:</span> {{ order.chassisNumber || '—' }}
                    </b-col>
                    <b-col md="4" class="so-ro">
                      <span>Số km:</span>
                      {{ order.odometer != null ? order.odometer : '—' }}
                    </b-col>
                    <b-col md="4" class="so-ro">
                      <span>Người mang xe:</span> {{ order.carBringer || '—' }}
                    </b-col>
                    <b-col md="4" class="so-ro">
                      <span>SĐT người mang xe:</span>
                      {{ order.carBringerPhone || '—' }}
                    </b-col>
                    <b-col md="4" class="so-ro">
                      <span>Giám định viên:</span>
                      {{ order.surveyorName || '—' }}
                    </b-col>
                    <b-col md="4" class="so-ro">
                      <span>Dự kiến giao xe:</span>
                      {{ formatDate(order.estimatedDeliveryTime) }}
                    </b-col>
                    <b-col md="4" class="so-ro">
                      <span>Khách xác nhận:</span>
                      <template v-if="order.customerConfirmedAt">
                        <i class="fas fa-check-circle text-success"></i>
                        {{ formatDate(order.customerConfirmedAt) }}
                      </template>
                      <template v-else>—</template>
                    </b-col>
                    <b-col md="12" class="so-ro">
                      <span>Yêu cầu khách hàng:</span>
                      {{ order.customerRequest || '—' }}
                    </b-col>
                  </b-row>
                </div>

                <!-- Section 2: Danh sách DV & phụ tùng (read-only) -->
                <div class="so-card mt-4">
                  <h6 class="so-card-title">Danh sách dịch vụ &amp; phụ tùng</h6>
                  <div class="table-responsive">
                    <table class="table table-bordered so-item-table">
                      <thead>
                        <tr>
                          <th style="width: 40px">STT</th>
                          <th style="width: 150px">Nhóm</th>
                          <th>Tên SP / Dịch vụ</th>
                          <th style="width: 80px">ĐVT</th>
                          <th class="text-right" style="width: 140px">Đơn giá</th>
                          <th class="text-center" style="width: 70px">SL</th>
                          <th class="text-right" style="width: 120px">Thành tiền</th>
                          <th class="text-right" style="width: 110px">CK phân bổ</th>
                          <th class="text-center" style="width: 80px">VAT</th>
                          <th class="text-right" style="width: 120px">Tiền VAT</th>
                          <th style="width: 130px">KTV</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(it, idx) in items" :key="idx">
                          <td class="text-center">{{ idx + 1 }}</td>
                          <td>{{ it.productGroupName || '—' }}</td>
                          <td>{{ it.productName }}</td>
                          <td>{{ it.unit }}</td>
                          <td class="text-right">{{ formatPrice(it.unitPrice) }}</td>
                          <td class="text-center">{{ it.quantity }}</td>
                          <td class="text-right">
                            {{ formatPrice(calc.lines[idx].subTotal) }}
                          </td>
                          <td class="text-right text-warning">
                            {{ formatPrice(calc.lines[idx].discountAllocation) }}
                          </td>
                          <td class="text-center">{{ it.vatType || '—' }}</td>
                          <td class="text-right">
                            {{ formatPrice(calc.lines[idx].vatAmount) }}
                          </td>
                          <td>{{ it.technicianName || '—' }}</td>
                        </tr>
                        <tr v-if="!items.length">
                          <td colspan="11" class="text-center text-muted py-3">
                            Chưa có dòng dịch vụ/phụ tùng.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Section 3: Thanh toán -->
                <div class="so-card mt-4">
                  <h6 class="so-card-title">Thông tin thanh toán</h6>
                  <b-row>
                    <b-col md="6">
                      <div class="so-total-row">
                        <span>Tổng trước chiết khấu</span>
                        <b>{{ formatPrice(calc.totals.totalBeforeDiscount) }}</b>
                      </div>
                      <div class="so-total-row text-warning">
                        <span>Tổng chiết khấu</span>
                        <b>- {{ formatPrice(calc.totals.totalDiscount) }}</b>
                      </div>
                      <div class="so-total-row">
                        <span>Tiền hàng (trước VAT)</span>
                        <b>{{ formatPrice(calc.totals.totalBeforeVAT) }}</b>
                      </div>
                      <div class="so-total-row">
                        <span>VAT (bóc tách)</span>
                        <b>{{ formatPrice(calc.totals.totalVAT) }}</b>
                      </div>
                      <div class="so-total-row so-total-final">
                        <span>TỔNG PHẢI THU (đã gồm VAT)</span>
                        <b>{{ formatPrice(calc.totals.totalAfterVAT) }}</b>
                      </div>
                      <div class="so-total-note">
                        Giá đã bao gồm VAT — VAT chỉ bóc tách để kê khai, không cộng thêm.
                      </div>
                    </b-col>
                    <b-col md="6">
                      <div class="so-total-row">
                        <span>Bảo hiểm thanh toán</span>
                        <b>{{ formatPrice(order.insurancePayAmount) }}</b>
                      </div>
                      <div class="so-total-row">
                        <span>Khách hàng thanh toán</span>
                        <b>{{ formatPrice(customerPayAmount) }}</b>
                      </div>
                    </b-col>
                  </b-row>
                </div>
              </b-tab>

              <!-- ---- Tab 2: Phiếu vật tư (MRQ — SO-006) ---- -->
              <!-- Ẩn ở giai đoạn báo giá (QUOTING/QUOTED) — MRQ chỉ phát sinh từ REPAIRING trở đi. -->
              <b-tab v-if="showMrqTab" title="Phiếu vật tư">
                <material-requisition-panel
                  ref="mrqPanel"
                  :service-order-id="id"
                  :so-status="order.status"
                  @changed="loadDetail"
                />
              </b-tab>

              <!-- ---- Tab: Quyết toán (SO-007 — Phase 5/6) ---- -->
              <!-- Chỉ hiện khi phiếu đang / đã quyết toán (SETTLING/SETTLED). -->
              <b-tab v-if="showSettlementTab" title="Quyết toán">
                <settlement-panel
                  ref="settlementPanel"
                  :service-order-id="id"
                  :so-status="order.status"
                  @changed="loadDetail"
                />
              </b-tab>

              <!-- ---- Tab 3: Lịch sử thao tác ---- -->
              <b-tab title="Lịch sử thao tác">
                <div class="so-card">
                  <b-overlay :show="historyLoading" rounded>
                    <div v-if="!histories.length" class="text-center text-muted py-4">
                      Chưa có lịch sử thao tác.
                    </div>
                    <ul v-else class="so-timeline">
                      <li
                        v-for="(h, i) in histories"
                        :key="h.id || i"
                        class="so-timeline-item"
                      >
                        <div class="so-timeline-dot"></div>
                        <div class="so-timeline-body">
                          <div class="so-timeline-head">
                            <b class="so-timeline-action">{{ h.action }}</b>
                            <span class="so-timeline-time">{{
                              formatDate(h.createdAt)
                            }}</span>
                          </div>
                          <div class="so-timeline-meta">
                            <span v-if="h.fromStatus" class="so-timeline-status">
                              {{ statusOf(h.fromStatus) }}
                              <i class="fas fa-arrow-right mx-1"></i>
                            </span>
                            <span class="so-timeline-status">{{
                              statusOf(h.toStatus)
                            }}</span>
                            <span class="so-timeline-actor">
                              · {{ actorName(h) }}</span
                            >
                          </div>
                          <div v-if="h.note" class="so-timeline-note">
                            {{ h.note }}
                          </div>
                        </div>
                      </li>
                    </ul>
                  </b-overlay>
                </div>
              </b-tab>
            </b-tabs>
          </div>
        </b-overlay>
      </template>
    </KTCodePreview>

    <!-- ============ Modal: Duyệt lệnh SC (khách xác nhận) ============ -->
    <b-modal
      ref="advance-modal"
      :title="pendingAction ? pendingAction.action : 'Tiến bước'"
      hide-footer
      centered
    >
      <p v-if="requiresConfirm">
        Xác nhận tiến phiếu sang
        <b>{{ pendingAction ? statusOf(pendingAction.to) : '' }}</b
        >.
      </p>
      <b-form-checkbox
        v-if="requiresConfirm"
        v-model="customerConfirmed"
        class="mb-3"
      >
        Khách hàng đã xác nhận báo giá
      </b-form-checkbox>
      <div class="d-flex justify-content-end">
        <b-button variant="secondary" class="mr-2" @click="closeAdvanceModal">
          Trở về
        </b-button>
        <b-button
          variant="primary"
          :disabled="(requiresConfirm && !customerConfirmed) || submitting"
          @click="confirmAdvance"
        >
          <b-spinner v-if="submitting" small /> Xác nhận
        </b-button>
      </div>
    </b-modal>

    <!-- ============ Modal: Lý do huỷ phiếu ============ -->
    <b-modal ref="cancel-modal" title="Huỷ phiếu dịch vụ" hide-footer centered>
      <b-form-group>
        <label>Lý do huỷ phiếu <span class="text-danger">*</span></label>
        <b-form-textarea
          v-model="cancelReason"
          size="sm"
          placeholder="Nhập lý do huỷ phiếu"
          :rows="3"
          :max-rows="4"
        />
      </b-form-group>
      <div class="d-flex justify-content-end">
        <b-button variant="secondary" class="mr-2" @click="closeCancelModal">
          Trở về
        </b-button>
        <b-button
          variant="danger"
          :disabled="!cancelReason || submitting"
          @click="confirmCancel"
        >
          <b-spinner v-if="submitting" small /> Xác nhận huỷ
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';
import { get } from 'lodash';
import Swal from 'sweetalert2';
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ServiceOrderService from '@/core/services/serviceOrder.service';
import MaterialRequisitionPanel from '@/view/components/service-orders/MaterialRequisitionPanel.vue';
import SettlementPanel from '@/view/components/service-orders/SettlementPanel.vue';
import {
  SO_STATUS,
  SO_STATUS_LABEL,
  SO_STATUS_VARIANT,
  SERVICE_TYPE_LABEL,
  DISCOUNT_TYPE,
  SO_STEPS,
  SO_STEP_INDEX,
  SO_PERMISSION,
  advanceActionsFrom,
  cancellableFrom,
} from '@/core/constants/serviceOrder';
import { convertPrice, makeToastSuccess, makeToastFaile } from '@/utils/common';
import { checkPermissions } from '@/utils/saveDataToLocal';
import { computeQuote, round2 } from '@/utils/serviceOrderCalc';

const num = (v) => Number(v) || 0;
// Index tab "Phiếu vật tư" (0 Phiếu dịch vụ · 1 Phiếu vật tư · 2 Lịch sử).
const MRQ_TAB_INDEX = 1;

export default {
  name: 'ServiceOrderDetail',
  components: { KTCodePreview, MaterialRequisitionPanel, SettlementPanel },
  props: {
    id: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      historyLoading: false,
      submitting: false,
      order: null,
      items: [],
      histories: [],
      steps: SO_STEPS,
      tabIndex: 0,
      pendingAction: null,
      customerConfirmed: false,
      cancelReason: '',
    };
  },
  computed: {
    statusLabel() {
      return this.order ? SO_STATUS_LABEL[this.order.status] : '';
    },
    statusVariant() {
      return this.order ? SO_STATUS_VARIANT[this.order.status] : 'secondary';
    },
    serviceTypeLabel() {
      return this.order ? SERVICE_TYPE_LABEL[this.order.serviceType] : '';
    },
    isCancelled() {
      return !!this.order && this.order.status === SO_STATUS.CANCELLED;
    },
    isReleased() {
      return !!this.order && this.order.status === SO_STATUS.RELEASED;
    },
    // Bước hiện tại (1..7) hoặc null (CANCELLED) — render thanh tiến trình.
    stepIndex() {
      return this.order ? SO_STEP_INDEX[this.order.status] : null;
    },
    // TẤT CẢ hành động tiến bước từ state machine (REPAIRING → 2 nút). Bỏ bước `mrqIssue` (phát vật
    // tư thực hiện ở bảng MRQ, không phải CTA trần). Mỗi nút kèm `canDo` (quyền tương ứng).
    advanceActions() {
      if (!this.order) return [];
      return advanceActionsFrom(this.order.status)
        .filter((t) => t.trigger !== 'mrqIssue')
        .map((t) => ({ ...t, canDo: checkPermissions([t.permission]) }));
    },
    requiresConfirm() {
      return (
        !!this.pendingAction &&
        this.pendingAction.requires === 'customerConfirmed'
      );
    },
    cancelRule() {
      return this.order
        ? cancellableFrom(this.order.status)
        : { allowed: false, enabled: false, phase: 0 };
    },
    // QUOTING/QUOTED → cho link sang màn Báo giá để sửa nội dung.
    showQuoteLink() {
      return (
        !!this.order &&
        (this.order.status === SO_STATUS.QUOTING ||
          this.order.status === SO_STATUS.QUOTED) &&
        checkPermissions([SO_PERMISSION.QUOTE_EDIT])
      );
    },
    canCancel() {
      return checkPermissions([SO_PERMISSION.CANCEL]);
    },
    // Tab "Phiếu vật tư": chỉ hiện từ REPAIRING trở đi (MRQ không phát sinh ở giai đoạn báo giá) +
    // có ít nhất 1 quyền MRQ. openMrqCreate chỉ gọi ở REPAIRING (tab chắc chắn có) → index 1 ổn định.
    showMrqTab() {
      if (!this.order) return false;
      const quoteStage =
        this.order.status === SO_STATUS.QUOTING ||
        this.order.status === SO_STATUS.QUOTED;
      return (
        !quoteStage &&
        checkPermissions([
          SO_PERMISSION.MRQ_VIEW,
          SO_PERMISSION.MRQ_CREATE,
          SO_PERMISSION.MRQ_ISSUE,
          SO_PERMISSION.MRQ_RETURN,
        ])
      );
    },
    // Tab "Quyết toán" (SO-007): hiện khi đang/đã quyết toán HOẶC đã xuất xưởng (RELEASED — xem lại
    // read-only) + có quyền xem/duyệt.
    showSettlementTab() {
      if (!this.order) return false;
      return (
        [
          SO_STATUS.SETTLING,
          SO_STATUS.SETTLED,
          SO_STATUS.RELEASED,
        ].includes(this.order.status) &&
        checkPermissions([SO_PERMISSION.SETTLE, SO_PERMISSION.SETTLE_VIEW])
      );
    },
    // Index tab "Quyết toán" (tabs động: 0 Phiếu dịch vụ · [Phiếu vật tư] · [Quyết toán] · Lịch sử).
    settlementTabIndex() {
      return 1 + (this.showMrqTab ? 1 : 0);
    },
    // Tính tiền read-only qua engine dùng chung (mirror BE so-calc).
    calc() {
      const r = computeQuote(
        this.items.map((it) => ({
          unitPrice: num(it.unitPrice),
          quantity: it.quantity,
          discountType: it.discountType,
          discountAmount: it.discountAmount,
          vatRate: it.vatRate,
        })),
        {
          discountType: this.order ? this.order.discountType : DISCOUNT_TYPE.AMOUNT,
          discountAmount: this.order ? this.order.discountAmount : 0,
          discountCouponType: this.order
            ? this.order.discountCouponType
            : DISCOUNT_TYPE.AMOUNT,
          discountCouponAmount: this.order ? this.order.discountCouponAmount : 0,
        },
      );
      return { lines: r.items, totals: r.totals };
    },
    customerPayAmount() {
      const ins = this.order ? num(this.order.insurancePayAmount) : 0;
      return round2(this.calc.totals.totalAfterVAT - ins);
    },
  },
  created() {
    this.loadDetail();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Phiếu dịch vụ', route: '/service-orders' },
      { title: 'Chi tiết' },
    ]);
  },
  methods: {
    formatDate(d) {
      return d ? moment(d).format('DD/MM/YYYY HH:mm') : '—';
    },
    formatPrice(v) {
      return convertPrice(num(v)) + ' đ';
    },
    statusOf(s) {
      return SO_STATUS_LABEL[s] || s || '—';
    },
    actorName(h) {
      return get(h, 'creator.fullName') || 'Hệ thống';
    },
    async loadDetail() {
      this.loading = true;
      try {
        const res = await ServiceOrderService.getById(this.id);
        const data = (res.data && res.data.data) || {};
        this.order = data;
        this.items = (data.items || []).map((it) => ({
          productName: it.productName,
          productGroupName: it.productGroupName || null,
          technicianName: it.technicianName || '',
          unit: it.unit || '',
          unitPrice: num(it.unitPrice),
          quantity: num(it.quantity),
          discountType: it.discountType || DISCOUNT_TYPE.AMOUNT,
          discountAmount: num(it.discountAmount),
          vatType: it.vatType || '0%',
          vatRate: num(it.vatRate),
        }));
      } catch (e) {
        makeToastFaile('Không tải được phiếu dịch vụ');
      } finally {
        this.loading = false;
      }
      this.loadHistory();
    },
    async loadHistory() {
      this.historyLoading = true;
      try {
        const res = await ServiceOrderService.getHistory(this.id);
        this.histories = (res.data && res.data.data) || [];
      } catch (e) {
        this.histories = [];
      } finally {
        this.historyLoading = false;
      }
    },
    // ---- Thanh tiến trình ----
    stepState(idx) {
      if (this.isReleased) return 'done'; // RELEASED → mọi bước ✓ (AC-07).
      if (!this.stepIndex) return 'muted'; // CANCELLED → toàn bộ mờ.
      if (idx < this.stepIndex) return 'done';
      if (idx === this.stepIndex) return 'current';
      return 'pending';
    },
    stepClass(idx) {
      return 'so-step--' + this.stepState(idx);
    },
    // ---- Tiến bước ----
    goQuote() {
      this.$router.push({ name: 'quote-service-order', params: { id: this.id } });
    },
    // Bấm 1 hành động CTA: Tạo MRQ → mở modal MRQ (tab Phiếu vật tư); còn lại → modal tiến bước.
    onActionClick(action) {
      if (!action.enabled || !action.canDo) return;
      if (action.trigger === 'mrqCreate') {
        this.openMrqCreate();
        return;
      }
      if (action.trigger === 'settlementStart') {
        this.onStartSettlement();
        return;
      }
      if (action.trigger === 'settlementConfirm') {
        this.onConfirmSettlement();
        return;
      }
      if (action.to === SO_STATUS.RELEASED) {
        this.onRelease();
        return;
      }
      this.pendingAction = action;
      this.customerConfirmed = false;
      this.$refs['advance-modal'].show();
    },
    // ---- Quyết toán (SO-007) ----
    // Bắt đầu quyết toán: INSPECTED → SETTLING + sinh dòng nháp (gọi /settlement/start, KHÔNG advance trần).
    async onStartSettlement() {
      const ok = await Swal.fire({
        title: 'Bắt đầu quyết toán?',
        html:
          'Hệ thống sẽ sinh <b>dòng nháp quyết toán</b> từ báo giá + vật tư đã phát.<br/>' +
          '<span class="text-muted">Bước này chỉ lập nháp phân bổ (tick Khách/BH) — chưa trừ tồn kho.</span>',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Bắt đầu',
        cancelButtonText: 'Trở về',
      });
      if (!ok || !ok.isConfirmed) return;
      this.submitting = true;
      try {
        await ServiceOrderService.startSettlement(this.id);
        makeToastSuccess('Đã bắt đầu quyết toán');
        await this.loadDetail();
        this.$nextTick(() => {
          this.tabIndex = this.settlementTabIndex;
        });
      } catch (e) {
        makeToastFaile(this.errMsg(e, 'Bắt đầu quyết toán thất bại'));
      } finally {
        this.submitting = false;
      }
    },
    // Duyệt quyết toán: SETTLING → SETTLED (gọi /settlement/confirm). Trừ tồn vật tư + sinh XKDV giá vốn.
    async onConfirmSettlement() {
      const ok = await Swal.fire({
        title: 'Duyệt quyết toán?',
        html:
          'Phiếu sẽ chuyển sang <b>Đã quyết toán</b>. Hệ thống sẽ <b>trừ tồn vật tư thực dùng</b> ' +
          'và <b>sinh phiếu xuất kho dịch vụ (giá vốn)</b>.<br/>' +
          '<span class="text-muted">Hoá đơn tạo riêng ở bước sau. Nhớ đã Lưu phân bổ trước khi duyệt.</span>',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Duyệt',
        cancelButtonText: 'Trở về',
      });
      if (!ok || !ok.isConfirmed) return;
      this.submitting = true;
      try {
        await ServiceOrderService.confirmSettlement(this.id);
        makeToastSuccess('Đã duyệt quyết toán');
        await this.loadDetail();
      } catch (e) {
        makeToastFaile(this.errMsg(e, 'Duyệt quyết toán thất bại'));
      } finally {
        this.submitting = false;
      }
    },
    // Xuất xưởng: SETTLED → RELEASED (giao xe + khoá phiếu). CẢNH BÁO mềm nếu chưa xuất hoá đơn
    // (Decision #10 — KHÔNG chặn). Sau xuất xưởng phiếu view-only, không sửa/huỷ/hoàn tác/tạo HĐ.
    async onRelease() {
      let noBill = false;
      try {
        const res = await ServiceOrderService.getSettlement(this.id);
        noBill = (get(res, 'data.data.bills', []) || []).length === 0;
      } catch (e) {
        noBill = false; // không rõ → không cảnh báo
      }
      const warn = noBill
        ? '<div class="text-danger mb-2"><i class="fas fa-exclamation-triangle"></i> ' +
          '<b>Phiếu chưa xuất hoá đơn nào.</b></div>'
        : '';
      const ok = await Swal.fire({
        title: 'Xuất xưởng?',
        html:
          warn +
          'Giao xe cho khách và <b>khoá phiếu</b>.<br/>' +
          '<span class="text-muted">Sau khi xuất xưởng KHÔNG sửa/huỷ/hoàn tác/tạo hoá đơn được nữa.</span>',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Xuất xưởng',
        cancelButtonText: 'Trở về',
      });
      if (!ok || !ok.isConfirmed) return;
      this.submitting = true;
      try {
        await ServiceOrderService.advance(this.id, { toStatus: SO_STATUS.RELEASED });
        makeToastSuccess('Đã xuất xưởng — phiếu đã khoá');
        await this.loadDetail();
      } catch (e) {
        makeToastFaile(this.errMsg(e, 'Xuất xưởng thất bại'));
      } finally {
        this.submitting = false;
      }
    },
    openMrqCreate() {
      this.tabIndex = MRQ_TAB_INDEX;
      this.$nextTick(() => {
        if (this.$refs.mrqPanel) this.$refs.mrqPanel.openCreate();
      });
    },
    closeAdvanceModal() {
      this.$refs['advance-modal'].hide();
    },
    async confirmAdvance() {
      const t = this.pendingAction;
      if (!t) return;
      if (this.requiresConfirm && !this.customerConfirmed) return;
      this.submitting = true;
      try {
        // Transition dùng quyền QUOTE_EDIT (Duyệt báo giá) → endpoint /approve;
        // còn lại đi qua /advance (BE chặn nếu gọi nhầm). State machine quyết định.
        if (t.permission === SO_PERMISSION.QUOTE_EDIT) {
          await ServiceOrderService.approveQuote(this.id);
        } else {
          await ServiceOrderService.advance(this.id, {
            toStatus: t.to,
            customerConfirmed: this.requiresConfirm
              ? this.customerConfirmed
              : undefined,
          });
        }
        makeToastSuccess(t.action + ' thành công');
        this.closeAdvanceModal();
        await this.loadDetail();
      } catch (e) {
        makeToastFaile(this.errMsg(e, t.action + ' thất bại'));
      } finally {
        this.submitting = false;
      }
    },
    // ---- Huỷ phiếu ----
    openCancelModal() {
      if (!this.cancelRule.enabled) return;
      this.cancelReason = '';
      this.$refs['cancel-modal'].show();
    },
    closeCancelModal() {
      this.$refs['cancel-modal'].hide();
    },
    async confirmCancel() {
      if (!this.cancelReason) return;
      this.submitting = true;
      try {
        await ServiceOrderService.cancel(this.id, this.cancelReason);
        makeToastSuccess('Huỷ phiếu thành công');
        this.closeCancelModal();
        await this.loadDetail();
      } catch (e) {
        makeToastFaile(this.errMsg(e, 'Huỷ phiếu thất bại'));
      } finally {
        this.submitting = false;
      }
    },
    errMsg(e, fallback) {
      return get(e, 'response.data.message') || fallback;
    },
  },
};
</script>

<style scoped>
.so-card {
  background: #fff;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 16px;
}
.so-card-title {
  font-weight: 600;
  color: #3f4254;
}
.so-ro {
  padding: 6px 12px;
  font-size: 0.9rem;
}
.so-ro span {
  color: #7e8299;
}
.so-detail-head {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.so-detail-no {
  font-family: monospace;
  font-weight: 600;
}
/* ---- Thanh tiến trình 7 bước (mẫu Metronic .wizard) ---- */
.so-wizard {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  background: #f9f9fb;
  border-radius: 6px;
  padding: 16px 8px;
}
.so-wizard-step {
  flex: 1;
  text-align: center;
  position: relative;
}
/* Đường nối giữa các bước. */
.so-wizard-step::before {
  content: '';
  position: absolute;
  top: 16px;
  left: -50%;
  width: 100%;
  height: 2px;
  background: #e4e6ef;
  z-index: 0;
}
.so-wizard-step:first-child::before {
  display: none;
}
.so-wizard-bullet {
  position: relative;
  z-index: 1;
  width: 32px;
  height: 32px;
  line-height: 32px;
  border-radius: 50%;
  margin: 0 auto 6px;
  background: #e4e6ef;
  color: #7e8299;
  font-weight: 600;
}
.so-wizard-label {
  font-size: 0.8rem;
  color: #7e8299;
}
.so-step--done .so-wizard-bullet {
  background: #1bc5bd;
  color: #fff;
}
.so-step--done::before {
  background: #1bc5bd;
}
.so-step--current .so-wizard-bullet {
  background: #3699ff;
  color: #fff;
}
.so-step--current .so-wizard-label {
  color: #3699ff;
  font-weight: 600;
}
.so-wizard--muted {
  opacity: 0.5;
}
/* ---- CTA bar ---- */
.so-cta-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
}
.so-phase-tag {
  font-size: 0.75rem;
  opacity: 0.8;
}
/* ---- Bảng items ---- */
.so-item-table {
  min-width: 1100px;
}
.so-item-table th {
  background: #f3f6f9;
  font-size: 12px;
  vertical-align: middle;
}
.so-item-table td {
  vertical-align: middle;
  padding: 6px 8px;
  font-size: 13px;
}
.so-total-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 0.95rem;
}
.so-total-final {
  border-top: 1px solid #ebedf3;
  margin-top: 4px;
  padding-top: 8px;
  font-size: 1.05rem;
  color: #1bc5bd;
}
.so-total-note {
  margin-top: 6px;
  font-size: 0.78rem;
  color: #b5b5c3;
  font-style: italic;
}
/* ---- Timeline lịch sử ---- */
.so-timeline {
  list-style: none;
  margin: 0;
  padding: 0 0 0 8px;
}
.so-timeline-item {
  position: relative;
  padding: 0 0 18px 24px;
  border-left: 2px solid #ebedf3;
}
.so-timeline-item:last-child {
  border-left-color: transparent;
  padding-bottom: 0;
}
.so-timeline-dot {
  position: absolute;
  left: -7px;
  top: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #3699ff;
  border: 2px solid #fff;
}
.so-timeline-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.so-timeline-action {
  color: #3f4254;
}
.so-timeline-time {
  font-size: 0.8rem;
  color: #b5b5c3;
}
.so-timeline-meta {
  font-size: 0.85rem;
  color: #7e8299;
  margin-top: 2px;
}
.so-timeline-note {
  margin-top: 4px;
  font-size: 0.85rem;
  color: #3f4254;
  font-style: italic;
}
</style>
